import { useEffect, useState, type FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { E_ShareholderStatuses } from '@/enums'
import { E_LsKeys } from '@/helpers/ls-keys'
import { rqKeys } from '@/react-query-keys'
import { apiRequest } from '@/helpers/request'
import { processPayment } from '@/helpers/payment'
import { getMs } from '@/helpers'
import { useOnlineStatus } from '@/helpers/hooks'
import { useAppStore } from '@/store'
import { profile } from '@/profile'
import { Button, Modal } from '@/components'
import type { T_UniRes } from '@/types'
import { BecomeShareholderStep1Drawer } from '../step1-drawer'
import { BecomeShareholderStep2Drawer } from '../step2-drawer'
import { BecomeShareholderStep3Drawer } from '../step3-drawer'
import { useBecomeShareholderStore } from '../store'


const SHAREHOLDER_FEE_PRODUCT_ID = 202
const BECOME_SHAREHOLDER_PENDING_TTL_MS = getMs('10m')

type T_BecomeShareholderPendingLs = {
  userId: number
  expiresAt: number
}

type T_BecomeShareholderPendingState = {
  isPending: boolean
  shouldRemove: boolean
  shouldMigrateLegacy: boolean
}

const getPendingBecomeShareholderState = (userId: number | null, raw: string | null): T_BecomeShareholderPendingState => {
  if (!userId || !raw) {
    return {
      isPending: false,
      shouldRemove: false,
      shouldMigrateLegacy: false,
    }
  }

  const now = Date.now()

  try {
    const parsed = JSON.parse(raw) as Partial<T_BecomeShareholderPendingLs>

    if (
      typeof parsed?.userId !== 'number'
      || typeof parsed?.expiresAt !== 'number'
      || parsed.userId !== userId
      || parsed.expiresAt <= now
    ) {
      return {
        isPending: false,
        shouldRemove: true,
        shouldMigrateLegacy: false,
      }
    }

    return {
      isPending: true,
      shouldRemove: false,
      shouldMigrateLegacy: false,
    }
  } catch {
    // fallback for legacy format where only userId string was stored
    if (raw === String(userId)) {
      return {
        isPending: true,
        shouldRemove: false,
        shouldMigrateLegacy: true,
      }
    }

    return {
      isPending: false,
      shouldRemove: true,
      shouldMigrateLegacy: false,
    }
  }
}


interface T_ShareholderAreaProps {
  hideBuyCertificateDrawer: () => void
}

export const ShareholderArea: FC<T_ShareholderAreaProps> = ({
  hideBuyCertificateDrawer,
}) => {
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()

  const userData = useAppStore((s) => s.userData)

  const showBecomeShareholderStep1Drawer = useBecomeShareholderStore((s) => s.showBecomeShareholderStep1Drawer)

  const isBecomeShareholderStep1DrawerVisible = useBecomeShareholderStore((s) => s.isBecomeShareholderStep1DrawerVisible)
  const isBecomeShareholderStep2DrawerVisible = useBecomeShareholderStore((s) => s.isBecomeShareholderStep2DrawerVisible)
  const isBecomeShareholderStep3DrawerVisible = useBecomeShareholderStore((s) => s.isBecomeShareholderStep3DrawerVisible)

  const [ isPaymentInProcess, setIsPaymentInProcess ] = useState<boolean>(false)
  const [ isNoFundsModalVisible, setIsNoFundsModalVisible ] = useState(false)

  const pendingBecomeShareholderRaw = userData.id
    ? localStorage.getItem(E_LsKeys.EraBecomeShareholderPendingUserId)
    : null
  const pendingBecomeShareholderState = getPendingBecomeShareholderState(
    userData.id,
    pendingBecomeShareholderRaw
  )
  const hasPendingBecomeShareholderRequest = pendingBecomeShareholderState.isPending

  useEffect(() => {
    if (!userData.id) return

    if (pendingBecomeShareholderState.shouldMigrateLegacy) {
      localStorage.setItem(
        E_LsKeys.EraBecomeShareholderPendingUserId,
        JSON.stringify({
          userId: userData.id,
          expiresAt: Date.now() + BECOME_SHAREHOLDER_PENDING_TTL_MS,
        })
      )
      return
    }

    if (pendingBecomeShareholderState.shouldRemove) {
      localStorage.removeItem(E_LsKeys.EraBecomeShareholderPendingUserId)
    }
  }, [
    userData.id,
    pendingBecomeShareholderState.shouldMigrateLegacy,
    pendingBecomeShareholderState.shouldRemove,
  ])

  useEffect(() => {
    if (!userData.id) return
    if (userData.shareholderStatusId === E_ShareholderStatuses.None) return

    localStorage.removeItem(E_LsKeys.EraBecomeShareholderPendingUserId)
  }, [ userData.id, userData.shareholderStatusId ])

  const showBecomeShareholderStep1DrawerOnlineGuard = () => {
    if (isOnline) {
      showBecomeShareholderStep1Drawer()
      return
    }

    toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
  }

  const payFee = async () => {
    setIsPaymentInProcess(true)

    // Создаем платёжный документ на выбранный сертификат
    const orderCreateRes = await apiRequest<T_UniRes<{ id: number }>>({
      method: 'delivery/order/create',
      param: {
        cart: [ { id: SHAREHOLDER_FEE_PRODUCT_ID, count: 1 } ],
      },
    })

    if (orderCreateRes.code === 'data-wrong') {
      toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
      setIsPaymentInProcess(false)
      return
    }

    const documentId = orderCreateRes?.data?.id

    if (!documentId) {
      toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
      setIsPaymentInProcess(false)
      return
    }

    const paymentInfoRes = await apiRequest<T_UniRes<any>>({
      method: 'money/payment/info',
      param: { documents: documentId },
    })

    if (paymentInfoRes.code !== 'ok') {
      toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
      return
    }

    const info = paymentInfoRes.data
    const required = Number(info?.value ?? info?.documents?.price ?? 0)
    const paid = Number(info?.paid ?? 0)
    const toPay = Math.max(0, required - paid)
    const moneys = (info?.methods || []).find((m: any) => m?.id === 'moneys-rur')
    const balance = Number(moneys?.balance ?? 0)

    if (toPay > balance) {
      setIsPaymentInProcess(false)
      setIsNoFundsModalVisible(true)
      return
    }

    await processPayment(documentId, toast)

    hideBuyCertificateDrawer()

    let tryCount: number = 0
    const MAX_TRY_COUNT = 7

    const intervalId = setInterval(() => {
      if (tryCount >= MAX_TRY_COUNT) clearInterval(intervalId)
      queryClient.invalidateQueries({ queryKey: [ rqKeys.userBalance ] })
      queryClient.invalidateQueries({ queryKey: [ rqKeys.userData ] })
      tryCount++
    }, 1000)

    setIsPaymentInProcess(false)
  }


  return <>
    {/* Стать пайщиком */}
    {userData.shareholderStatusId === E_ShareholderStatuses.None
      && !(userData?.sourceCompanyId !== 0 && profile.functionalAll)
      && !hasPendingBecomeShareholderRequest && (
      <Button
        $isBlack
        block
        onClick={showBecomeShareholderStep1DrawerOnlineGuard}
      >
        Стать пайщиком
      </Button>
    )}

    {/* Отправлена заявка на статус пайщика */}
    {userData.shareholderStatusId === E_ShareholderStatuses.Want && (
      <Button
        block
        $isBlack
        onClick={payFee}
        loading={isPaymentInProcess}
        disabled={isPaymentInProcess}
      >
        {isPaymentInProcess ? 'Идёт оплата..' : 'Оплатить взнос' }
      </Button>
    )}

    {/* Оплата взноса произведена */}
    {userData.shareholderStatusId === E_ShareholderStatuses.Waiting && <>
      <Button block loading>
        Ваша заявка на подтверждении
      </Button>

      <br />

      <p>Скоро с вами свяжутся</p>
    </>}

    {/* Модалки 'Стать пайщиком' */}

    {isBecomeShareholderStep1DrawerVisible && (
      <BecomeShareholderStep1Drawer />
    )}

    {isBecomeShareholderStep2DrawerVisible && (
      <BecomeShareholderStep2Drawer />
    )}

    {isBecomeShareholderStep3DrawerVisible && (
      <BecomeShareholderStep3Drawer
        isPaymentInProcess={isPaymentInProcess}
        payFee={payFee}
      />
    )}

    {/* Недостаточно средств */}
    {
      <Modal
        open={isNoFundsModalVisible}
        centered
        closable={false}
        footer={<Button key='ok' type='primary' block onClick={() => setIsNoFundsModalVisible(false)}>Хорошо</Button>}
      >
        <h3>На счету недостаточно средств!</h3>
      </Modal>
    }

  </>
}
